import { Box, Button, Heading, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { useCallback, useMemo, useState } from 'react';

import {
  FormContentContainer,
  FormFooterContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { AddressForm } from '@duke-hq/google-address/components/AddressForm';
import type {
  AddressFormData,
  AddressFormProps,
  AddressSubmissionData,
} from '@duke-hq/google-address/types';
import { isValidAustralianState } from '@duke-hq/google-address/services/validation/australianStates';

const ADDRESS_FORM_ID = 'admin-student-address-form';

export type AddressAutocompleteFormData = AddressSubmissionData & {
  addressInput?: string;
};

interface AddressAutocompleteFormProps {
  isEdit?: boolean;
  isSameAsCurrent: boolean;
  googleAddressApiKey: string;
  addressFields: AddressFormData & { addressInput?: string };
  // The returned promise must reject on save failure so AddressForm can turn a
  // postcode-confirmation rejection into its inline "save anyway" warning.
  onUpdate: (data: AddressAutocompleteFormData) => void | Promise<void>;
  getAddressSuggestions?: AddressFormProps['getAddressSuggestions'];
}

export default function AddressAutocomplete({
  isEdit = false,
  isSameAsCurrent,
  googleAddressApiKey,
  addressFields,
  onUpdate,
  getAddressSuggestions,
}: AddressAutocompleteFormProps): React.ReactElement {
  // Validate state fields
  const addressState = isValidAustralianState(addressFields.addressState)
    ? addressFields.addressState
    : undefined;

  const postalAddressState = isValidAustralianState(addressFields.postalAddressState)
    ? addressFields.postalAddressState
    : undefined;

  const {
    formattedAddress,
    addressInput,
    addressPostcode,
    addressLocality,
    addressStreetName,
    addressStreetNumber,
    addressStreetSubpremise,
    sameAsCurrent,
    postalAddressStreetName,
    postalAddressLocality,
    postalAddressPostcode,
    postalAddressStreetSubpremise,
  } = addressFields;

  const initialData: Partial<AddressFormData> = useMemo(
    () => ({
      address: formattedAddress ?? addressInput ?? '',
      addressState,
      addressPostcode,
      addressLocality,
      addressStreetName,
      addressStreetNumber,
      addressStreetSubpremise,
      formattedAddress,
      sameAsCurrent,
      postalAddressStreetName,
      postalAddressLocality,
      postalAddressState,
      postalAddressPostcode,
      postalAddressStreetSubpremise,
    }),
    [
      formattedAddress,
      addressInput,
      addressState,
      addressPostcode,
      addressLocality,
      addressStreetName,
      addressStreetNumber,
      addressStreetSubpremise,
      sameAsCurrent,
      postalAddressStreetName,
      postalAddressLocality,
      postalAddressState,
      postalAddressPostcode,
      postalAddressStreetSubpremise,
    ],
  );

  const handleSubmit = useCallback(
    async (data: AddressSubmissionData) => {
      // Convert AddressSubmissionData to AddressAutocompleteFormData format
      const addressData: AddressAutocompleteFormData = {
        addressInput: data.address,
        addressState: data.addressState,
        addressPostcode: data.addressPostcode,
        addressLocality: data.addressLocality,
        addressStreetName: data.addressStreetName,
        addressStreetNumber: data.addressStreetNumber,
        addressStreetSubpremise: data.addressStreetSubpremise,
        formattedAddress: data.formattedAddress,
        postalAddressStreetName: data.postalAddressStreetName,
        postalAddressLocality: data.postalAddressLocality,
        postalAddressState: data.postalAddressState,
        postalAddressPostcode: data.postalAddressPostcode,
        postalAddressStreetSubpremise: data.postalAddressStreetSubpremise,
        sameAsCurrent: data.sameAsCurrent,
        confirmedAddressWarnings: data.confirmedAddressWarnings,
      };

      await onUpdate(addressData);
    },
    [onUpdate],
  );

  const [isSubmittable, setIsSubmittable] = useState(true);
  const [hasPendingWarnings, setHasPendingWarnings] = useState(false);

  return (
    <VStack w="full" align="stretch" spacing={0} bg="bg.surface">
      <FormContentContainer>
        <Box style={{ width: '100%' }}>
          <Heading fontSize="lg" size="xs" w="full" pb={4}>
            <Trans>Update Student Address</Trans>
          </Heading>

          <AddressForm
            googleApiKey={googleAddressApiKey}
            initialData={initialData}
            onSubmit={handleSubmit}
            showPostalAddress={!isSameAsCurrent}
            validationMode="strict"
            isEditMode={isEdit}
            showTitle={false}
            formId={ADDRESS_FORM_ID}
            onSubmittableChange={setIsSubmittable}
            onPendingWarningsChange={setHasPendingWarnings}
            getAddressSuggestions={getAddressSuggestions}
          />
        </Box>
      </FormContentContainer>

      <FormFooterContainer>
        <Button
          type="submit"
          form={ADDRESS_FORM_ID}
          isDisabled={!isSubmittable}
          colorScheme={hasPendingWarnings ? 'orange' : undefined}
        >
          {hasPendingWarnings ? <Trans>Save anyway</Trans> : <Trans>Update</Trans>}
        </Button>
      </FormFooterContainer>
    </VStack>
  );
}
